import React from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  Tooltip,
  alpha,
  useTheme,
  ThemeProvider,
  darkTheme,
} from 'omaha-design-system';
import { ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { PlayerPropSportsbook } from '../data/types';
import { useAllBooksSportsbooks } from '../hooks/useAllBooksSportsbooks';
import { useSportsbookNames } from '../hooks/useSportsbookNames';
import { isActualMobileDevice, getPlatformDeepLink } from '../data/deeplinkHelper';
import { PropBet } from './columns';

interface ExpandedOddsPanelProps {
  row: PropBet;
}

export const SportsbookCard: React.FC<{
  sb: PlayerPropSportsbook;
}> = ({ sb }) => {
  const theme = useTheme();
  const { getName } = useSportsbookNames();
  const sportsbookName = getName(sb.book);

  const evColor = sb.evPct > 0.5
    ? theme.palette.success.main
    : sb.evPct < -0.5
      ? theme.palette.error.main
      : alpha(theme.palette.common.white, 0.7);

  const formatEv = (ev: number) => {
    const rounded = Math.round(ev * 10) / 10;
    if (Math.abs(rounded) < 1) return `<1%`;
    return `${rounded.toFixed(0)}%`;
  };

  const handleClick = () => {
    if (sb.deepLinkUrl) {
      const isMobile = isActualMobileDevice();
      const betSlipItem = {
        id: '',
        playerName: '',
        avatar: '',
        stat: '',
        projection: '',
        bookLine: '',
        edge: '',
        bookWin: sb.bookWin,
        modelWin: sb.modelWin,
        evPct: sb.evPct,
        book: sb.book,
        bookLogo: sb.bookIcon,
        odds: sb.americanOdds,
        side: sb.side,
        deepLinkUrl: sb.deepLinkUrl,
      };
      const url = getPlatformDeepLink([betSlipItem], isMobile);
      if (url) {
        window.open(url, '_blank');
      }
    }
  };

  return (
    <Paper
      elevation={1}
      onClick={handleClick}
      sx={{
        borderRadius: '6px',
        cursor: sb.deepLinkUrl ? 'pointer' : 'default',
        '&:hover': sb.deepLinkUrl ? {
          backgroundColor: alpha(theme.palette.common.white, 0.05),
        } : {},
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1, py: 0.75, minWidth: 160 }}
      >
        {sb.bookIcon && (
          <Tooltip title={sportsbookName} arrow>
            <Box
              component="img"
              src={sb.bookIcon}
              alt={sportsbookName}
              sx={{
                width: 28,
                height: 28,
                borderRadius: '4px',
                objectFit: 'contain',
              }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </Tooltip>
        )}
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: 12,
            color: alpha(theme.palette.text.primary, 0.7),
          }}
        >
          {sb.side === 'Under' ? 'u' : 'o'}{sb.line}
        </Typography>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: 12,
            color: alpha(theme.palette.text.primary, 0.7),
          }}
        >
          {sb.americanOdds}
        </Typography>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: 12,
            color: evColor,
          }}
        >
          ({formatEv(sb.evPct)})
        </Typography>
        {sb.delta > 0 && <ArrowUp size={14} weight="bold" color={theme.palette.success.main} />}
        {sb.delta < 0 && <ArrowDown size={14} weight="bold" color={theme.palette.error.main} />}
      </Stack>
    </Paper>
  );
};

export const ExpandedOddsPanel: React.FC<ExpandedOddsPanelProps> = ({ row }) => {
  const { getSportsbooks, isLoading } = useAllBooksSportsbooks();

  // Match this row to the all-books data, sorted by EV
  const sortedBooks = React.useMemo(
    () => getSportsbooks(row.playerName, row.originalStat, row.side, row.sportsbooks),
    [getSportsbooks, row],
  );

  return (
    <ThemeProvider theme={darkTheme}>
      <Box
        sx={{
          pl: '60px',
          pr: 2,
          py: 1.5,
          backgroundColor: '#1a1a1b',
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 60 }}>
            <CircularProgress size={20} />
          </Box>
        ) : sortedBooks.length === 0 ? (
          <Box sx={{ minHeight: 60, display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No sportsbook odds available.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {sortedBooks.map((sb) => (
              <SportsbookCard
                key={sb.book}
                sb={sb}
              />
            ))}
          </Box>
        )}
      </Box>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.05)' }} />
    </ThemeProvider>
  );
};
